import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  LinearProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid
} from '@mui/material'
import {
  History,
  Person,
  Edit,
  Verified,
  Close,
  Info
} from '@mui/icons-material'
import { useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { format } from 'date-fns'
import medicalCodingService from '../../../services/medicalCodingService'

const CodingAuditTrail = () => {
  const { codingId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  
  const [loading, setLoading] = useState(false)
  const [auditTrail, setAuditTrail] = useState([])
  const [selectedEntry, setSelectedEntry] = useState(null)
  const [detailsOpen, setDetailsOpen] = useState(false)

  useEffect(() => {
    if (codingId) {
      loadAuditTrail()
    }
  }, [codingId])

  const loadAuditTrail = async () => {
    setLoading(true)
    try {
      const response = await medicalCodingService.getCodingAuditTrail(codingId)
      
      if (response.data.success) {
        setAuditTrail(response.data.data?.auditTrail || [])
      }
    } catch (error) {
      enqueueSnackbar('Failed to load audit trail', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const getActionIcon = (action) => {
    switch (action) {
      case 'CREATE':
        return <Edit color="primary" />
      case 'UPDATE':
        return <Edit color="info" />
      case 'VERIFICATION':
        return <Verified color="success" />
      case 'DELETE':
        return <Close color="error" />
      default:
        return <Info color="default" />
    }
  }

  const getActionColor = (action) => {
    switch (action) {
      case 'CREATE':
        return 'primary'
      case 'UPDATE':
        return 'info'
      case 'VERIFICATION':
        return 'success'
      case 'DELETE':
        return 'error'
      default:
        return 'default'
    }
  }

  const handleViewDetails = (entry) => {
    setSelectedEntry(entry)
    setDetailsOpen(true)
  }

  if (loading) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Loading Audit Trail...
        </Typography>
        <LinearProgress />
      </Box>
    )
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <History sx={{ mr: 2 }} />
        <Typography variant="h6" fontWeight={600}>
          Coding Audit Trail
        </Typography>
      </Box>

      {auditTrail.length === 0 ? (
        <Alert severity="info">
          No audit trail entries found for this coding record.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Action</TableCell>
                <TableCell>Field</TableCell>
                <TableCell>Changes</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Date & Time</TableCell>
                <TableCell>IP Address</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {auditTrail.map((entry, index) => (
                <TableRow key={index} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {getActionIcon(entry.action)}
                      <Chip
                        label={entry.action}
                        color={getActionColor(entry.action)}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {entry.field}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Box>
                      {entry.oldValue && (
                        <Typography variant="caption" color="error.main">
                          From: {JSON.stringify(entry.oldValue)}
                        </Typography>
                      )}
                      {entry.newValue && (
                        <Typography variant="caption" color="success.main" display="block">
                          To: {JSON.stringify(entry.newValue)}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ width: 24, height: 24, mr: 1 }}>
                        <Person fontSize="small" />
                      </Avatar>
                      <Typography variant="body2">
                        {entry.by?.name || 'System'}
                      </Typography>
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2">
                      {entry.timestamp ? format(new Date(entry.timestamp), 'MMM dd, yyyy HH:mm:ss') : 'N/A'}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {entry.ipAddress || 'N/A'}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Button
                      size="small"
                      onClick={() => handleViewDetails(entry)}
                    >
                      Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Audit Entry Details
        </DialogTitle>
        <DialogContent>
          {selectedEntry && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Action
                </Typography>
                <Chip
                  label={selectedEntry.action}
                  color={getActionColor(selectedEntry.action)}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Field Modified
                </Typography>
                <Typography variant="body2">
                  {selectedEntry.field}
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  User
                </Typography>
                <Typography variant="body2">
                  {selectedEntry.by?.name || 'System'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedEntry.by?.email}
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Timestamp
                </Typography>
                <Typography variant="body2">
                  {selectedEntry.timestamp ? format(new Date(selectedEntry.timestamp), 'MMMM dd, yyyy HH:mm:ss') : 'N/A'}
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  IP Address
                </Typography>
                <Typography variant="body2">
                  {selectedEntry.ipAddress || 'N/A'}
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  User Agent
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                  {selectedEntry.userAgent || 'N/A'}
                </Typography>
              </Grid>
              
              {selectedEntry.oldValue && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Previous Value
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>
                    <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                      {JSON.stringify(selectedEntry.oldValue, null, 2)}
                    </pre>
                  </Paper>
                </Grid>
              )}
              
              {selectedEntry.newValue && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    New Value
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: 'success.light', color: 'success.contrastText' }}>
                    <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                      {JSON.stringify(selectedEntry.newValue, null, 2)}
                    </pre>
                  </Paper>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default CodingAuditTrail